import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Four self-hosted font families, switchable at runtime from the
 * Preferences modal :
 *
 *   - geist   : Geist (sans) + JetBrains Mono (mono)   — DS default
 *   - inter   : Inter (sans) + JetBrains Mono (mono)   — classic UI
 *   - plex    : IBM Plex Sans + IBM Plex Mono           — lab / enterprise
 *   - system  : native system-ui stack                  — zero download
 *
 * All sources are served by FastAPI from /static/vendor/fonts/ at runtime.
 */
export type FontFamily = 'geist' | 'inter' | 'plex' | 'system';

export const FONT_FAMILIES: readonly FontFamily[] = ['geist', 'inter', 'plex', 'system'] as const;

export const FONT_FAMILY_LABELS: Record<FontFamily, {
  name: string;
  sans: string;
  mono: string;
  hint: string;
  /** inline CSS font-family for the tile preview */
  previewSans: string;
  previewMono: string;
}> = {
  geist: {
    name: 'Geist',
    sans: 'Geist',
    mono: 'JetBrains Mono',
    hint: 'Défaut · Design System',
    previewSans: "'Geist', sans-serif",
    previewMono: "'JetBrains Mono', monospace",
  },
  inter: {
    name: 'Inter',
    sans: 'Inter',
    mono: 'JetBrains Mono',
    hint: 'Haute lisibilité low-DPI',
    previewSans: "'Inter', sans-serif",
    previewMono: "'JetBrains Mono', monospace",
  },
  plex: {
    name: 'IBM Plex',
    sans: 'IBM Plex Sans',
    mono: 'IBM Plex Mono',
    hint: 'Registre lab / entreprise',
    previewSans: "'IBM Plex Sans', sans-serif",
    previewMono: "'IBM Plex Mono', monospace",
  },
  system: {
    name: 'Système',
    sans: 'system-ui',
    mono: 'ui-monospace',
    hint: 'Natif OS · zéro téléchargement',
    previewSans: 'system-ui, sans-serif',
    previewMono: 'ui-monospace, monospace',
  },
};

interface FontFamilyState {
  fontFamily: FontFamily;
  setFontFamily: (f: FontFamily) => void;
}

function applyFontFamily(f: FontFamily): void {
  if (typeof document === 'undefined') return;
  if (f === 'geist') {
    document.documentElement.removeAttribute('data-fontfamily');
  } else {
    document.documentElement.setAttribute('data-fontfamily', f);
  }
}

export const useFontFamilyStore = create<FontFamilyState>()(
  persist(
    (set) => ({
      fontFamily: 'geist',
      setFontFamily: (f) => {
        applyFontFamily(f);
        set({ fontFamily: f });
      },
    }),
    {
      name: 'lab.fontfamily',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) applyFontFamily(state.fontFamily);
      },
    },
  ),
);
